/**
 * Lightweight client-side router (PJAX) for same-origin links.
 *
 * Keeps the persistent page shell (header, footer, audio, theme) alive and
 * swaps only <main id="main-content">. After a successful swap it dispatches
 * a custom `page:load` event so other scripts can reinitialize page-specific
 * widgets. `page:load` is dispatched on BOTH initial load and router
 * navigation, so it is the single init signal — page scripts should listen to
 * `page:load` only, not also `DOMContentLoaded`.
 *
 * Locale is global shell state (header, footer, nav, aria-labels), so a
 * locale change triggers a full page navigation rather than an SPA swap —
 * this re-renders the entire page correctly instead of patching the shell
 * piecemeal. Same-locale navigation stays as an SPA swap.
 */
(function () {
  if (!window.history || !window.fetch || !window.DOMParser) return;

  // Prevent the browser from restoring scroll positions on history entries;
  // the router saves/restores scroll manually instead.
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
  // Give the initial entry a state so popstate can be handled
  history.replaceState({ url: location.pathname + location.search }, '', location.href);

  const MAIN_SELECTOR = 'main#main-content';
  const LIVE_REGION_ID = 'page-announcer';
  let isNavigating = false;

  // Prefetched same-locale documents, keyed by pathname+search, value = a promise
  // resolving to the HTML string (or null). Starting the fetch before the tap
  // (on hover / touchstart, or after load for the bottom nav) keeps the network
  // round-trip off the navigation critical path — the tab appears instantly.
  const prefetchCache = new Map();
  const PREFETCH_LIMIT = 8;

  function prefetchKey(href) {
    const url = resolveUrl(href);
    if (!url) return null;
    if (url.origin !== window.location.origin) return null;
    if (localeOf(url) !== currentLocale()) return null;
    const key = url.pathname + url.search;
    if (key === location.pathname + location.search) return null;
    return key;
  }

  function prefetch(href) {
    const key = prefetchKey(href);
    if (!key || prefetchCache.has(key)) return;
    const promise = fetch(key, { credentials: 'same-origin' })
      .then((res) => (res.ok && !res.redirected ? res.text() : null))
      .catch(() => null);
    prefetchCache.set(key, promise);
    // Keep the cache small (drop the oldest entry).
    if (prefetchCache.size > PREFETCH_LIMIT) {
      prefetchCache.delete(prefetchCache.keys().next().value);
    }
  }

  function prefetchFromEvent(event) {
    const link = event.target.closest('a[href]');
    if (link && isInternalLink(link)) prefetch(link.href);
  }

  function prefetchNav() {
    document
      .querySelectorAll('.bottom-nav a[href], .site-nav a[href]')
      .forEach((a) => prefetch(a.href));
  }

  function resolveUrl(href) {
    try {
      return new URL(href, window.location.href);
    } catch {
      return null;
    }
  }

  function isSamePage(url) {
    return (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search
    );
  }

  // Locale is derived from the first path segment (e.g. /en/..., /ru/...).
  function localeOf(url) {
    return (url.pathname.split('/').filter(Boolean)[0] || '').toLowerCase();
  }

  function currentLocale() {
    return (document.documentElement.lang || '').toLowerCase();
  }

  function isInternalLink(link) {
    if (!link || !link.href) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    const url = resolveUrl(link.href);
    if (!url) return false;
    if (url.origin !== window.location.origin) return false;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return true;
  }

  function interceptClick(event) {
    const link = event.target.closest('a[href]');
    if (!link) return;
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    if (!isInternalLink(link)) return;

    const url = resolveUrl(link.href);
    if (!url) return;

    // Same page: a hash anchor is handled by the browser; a plain same-page
    // link (e.g. tapping the active tab) should NOT reload and should NOT
    // jump to the top — just leave the page as-is.
    if (isSamePage(url)) {
      if (!url.hash) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
    navigate(url.pathname + url.search, { push: true });
  }

  async function navigate(url, { push = false } = {}) {
    // A locale change affects the whole shell (header, footer, nav, aria-labels),
    // so patch it piecemeal. Do a full page navigation instead — everything
    // re-renders correctly in the new locale. Centralized here so every nav
    // path (link click, popstate) goes through the same decision.
    const target = resolveUrl(url);
    if (target && localeOf(target) && localeOf(target) !== currentLocale()) {
      window.location.assign(url);
      return;
    }

    if (isNavigating) return;
    isNavigating = true;

    try {
      // Use a prefetched copy when we have one; only hit the network otherwise.
      let html = prefetchCache.has(url) ? await prefetchCache.get(url) : null;
      if (typeof html !== 'string') {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // The server redirected us (e.g. /me/ → /me/signup/ for signed-out
        // users). Do a real navigation so the URL, shell, and scripts stay canonical.
        if (response.redirected) {
          window.location.assign(response.url);
          return;
        }

        html = await response.text();
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newMain = doc.querySelector(MAIN_SELECTOR);
      if (!newMain) throw new Error('Missing <main id="main-content"> in response');

      const currentMain = document.querySelector(MAIN_SELECTOR);
      if (!currentMain) throw new Error('Missing <main id="main-content"> on current page');

      // Swap content with a plain DOM replace — no View Transition. Keeping the
      // shell (and the bottom nav) live means a tab tap is immediate, instead of
      // the bar re-compositing (which let content show through it for a frame).
      const swapContent = () => {
        currentMain.innerHTML = newMain.innerHTML;
        // Reveal in-view content synchronously so it doesn't flash at opacity:0.
        currentMain.classList.add('is-swap');
        currentMain.querySelectorAll('.reveal').forEach((el) => {
          if (el.getBoundingClientRect().top < window.innerHeight) {
            el.classList.add('is-visible');
          }
        });
      };
      swapContent();

      // Update shell metadata.
      const newTitle = doc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent || document.title;

      if (doc.documentElement.lang) {
        document.documentElement.lang = doc.documentElement.lang;
      }
      const newDir = doc.documentElement.getAttribute('dir');
      if (newDir) document.documentElement.setAttribute('dir', newDir);

      const newPageKey = doc.body ? doc.body.dataset.pageKey : '';
      document.body.dataset.pageKey = newPageKey;
      const newLocale = doc.body ? doc.body.dataset.locale : '';
      document.body.dataset.locale = newLocale;

      updateActiveNavLink(url);

      if (push) {
        history.pushState({ url }, '', url);
      }

      // Start at the top. Temporarily disable smooth snap scrolling so the reset
      // isn't fought by an in-progress animation, then restore it — only on
      // pages that actually use snap sections, or Chrome re-snaps to the old
      // position.
      const htmlEl = document.documentElement;
      const originalScrollBehavior = htmlEl ? htmlEl.style.scrollBehavior : '';
      const originalScrollSnapType = htmlEl ? htmlEl.style.scrollSnapType : '';
      const hasSnapSections = !!doc.querySelector('.section--snap');
      if (htmlEl) {
        htmlEl.style.scrollBehavior = 'auto';
        htmlEl.style.scrollSnapType = 'none';
      }
      window.scrollTo(0, 0);
      requestAnimationFrame(() => {
        currentMain.classList.remove('is-swap');
        if (!htmlEl) return;
        htmlEl.style.scrollBehavior = originalScrollBehavior;
        if (hasSnapSections) {
          htmlEl.style.scrollSnapType = originalScrollSnapType;
        }
      });
      currentMain.tabIndex = -1;
      currentMain.focus({ preventScroll: true });

      announcePage(document.title);
      // Defer page:load to next tick so focus/scroll settle first
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('page:load', { detail: { url } }));
      }, 0);
    } catch (error) {
      // Fall back to a normal navigation so the site still works.
      window.location.assign(url);
    } finally {
      isNavigating = false;
    }
  }

  function updateActiveNavLink(url) {
    const pathname = resolveUrl(url).pathname;
    document.querySelectorAll('.site-nav a[href], .bottom-nav a[href]').forEach((link) => {
      const linkUrl = resolveUrl(link.href);
      if (linkUrl && linkUrl.pathname === pathname) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function announcePage(title) {
    let region = document.getElementById(LIVE_REGION_ID);
    if (!region) {
      region = document.createElement('div');
      region.id = LIVE_REGION_ID;
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = `Navigated to ${title}`;
    });
  }

  document.addEventListener('click', interceptClick);
  // Prefetch on intent so the round-trip is already paid when the tap lands.
  document.addEventListener('mouseover', prefetchFromEvent, { passive: true });
  document.addEventListener('touchstart', prefetchFromEvent, { passive: true });
  document.addEventListener('page:load', () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchNav, { timeout: 2000 });
    } else {
      setTimeout(prefetchNav, 800);
    }
  });

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.url) {
      navigate(event.state.url, { push: false });
    }
  });

  // Dispatch the initial page:load once the document is ready so modules can
  // initialize consistently on both full loads and router swaps.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.dispatchEvent(
        new CustomEvent('page:load', { detail: { url: window.location.href } })
      );
    });
  } else {
    document.dispatchEvent(
      new CustomEvent('page:load', { detail: { url: window.location.href } })
    );
  }
}());
